<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { authAPI, type WeeklyScheduleEntry } from '../services/api';
import { serverMessage } from '../composables/useVisits';
import { useAuth } from '../composables/useAuth';

const { t } = useI18n();
const { user, fetchUser } = useAuth();

type ScheduleRow = { working: boolean; start: string; end: string };

const DEFAULT_START = '09:00';
const DEFAULT_END = '18:00';

// Monday first for the UI; the payload is always indexed 0..6 (0 = Sunday),
// the shape the availability engine expects.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

const TIME_INPUT_CLASS =
  'px-3 py-2 bg-[#1E1E23] border rounded-2xl text-sm text-[#F5F0EA] [color-scheme:dark] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition disabled:opacity-50 disabled:cursor-not-allowed';

const rows = ref<ScheduleRow[]>([]);
const rowErrors = ref<Record<number, string>>({});
const isSaving = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

// Hours stay in the row even when a day is switched off, so toggling it back
// does not lose what was typed. They are dropped when the payload is built.
// Indexed by day of week, so rows[day] lines up with the payload the API expects.
const buildRows = (schedule: WeeklyScheduleEntry[] | null | undefined): ScheduleRow[] =>
  Array.from({ length: 7 }, (_, day) => {
    const entry = schedule?.find((candidate) => candidate.day === day);

    return {
      working: entry ? entry.working : day !== 0 && day !== 6,
      start: entry?.start ?? DEFAULT_START,
      end: entry?.end ?? DEFAULT_END,
    };
  });

watch(
  user,
  (current) => {
    rows.value = buildRows(current?.weekly_schedule);
  },
  { immediate: true },
);

const validate = (): boolean => {
  rowErrors.value = {};

  rows.value.forEach((row, day) => {
    if (!row.working) return;

    if (!row.start || !row.end) {
      rowErrors.value[day] = t('settings.error_schedule_hours_required');
      return;
    }

    if (row.end <= row.start) {
      rowErrors.value[day] = t('settings.error_schedule_end_after_start');
    }
  });

  return Object.keys(rowErrors.value).length === 0;
};

const buildPayload = (): WeeklyScheduleEntry[] =>
  rows.value.map((row, day) =>
    row.working ? { day, working: true, start: row.start, end: row.end } : { day, working: false },
  );

const handleSubmit = async (): Promise<void> => {
  successMessage.value = '';
  errorMessage.value = '';

  if (!validate()) return;

  isSaving.value = true;

  try {
    // The step is edited on its own card and left out here, which the endpoint reads
    // as "leave it as it was".
    await authAPI.updateWeeklySchedule({ schedule: buildPayload() });
    await fetchUser();
    successMessage.value = t('settings.schedule_save_success');
  } catch (err) {
    errorMessage.value = serverMessage(err) || t('settings.schedule_save_error');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="bg-[#111113] rounded-2xl border border-[#2A2018] p-6 w-full">
    <h3 class="text-base font-semibold text-[#F5F0EA] mb-2">
      {{ $t('settings.schedule_section') }}
    </h3>
    <p class="text-sm text-[#524840] mb-6">{{ $t('settings.schedule_hint') }}</p>

    <div class="space-y-3">
      <div v-for="day in DAY_ORDER" :key="day">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="w-32 shrink-0 text-sm text-[#F5F0EA]">
            {{ $t(`settings.schedule_day_${day}`) }}
          </span>

          <label class="flex items-center gap-2 w-28 shrink-0 cursor-pointer">
            <input
              type="checkbox"
              v-model="rows[day].working"
              :disabled="isSaving"
              class="w-4 h-4 accent-[#D4956A]"
            />
            <span class="text-sm" :class="rows[day].working ? 'text-[#9E9080]' : 'text-[#524840]'">
              {{
                rows[day].working
                  ? $t('settings.schedule_working')
                  : $t('settings.schedule_day_off')
              }}
            </span>
          </label>

          <div class="flex items-center gap-2" :class="!rows[day].working && 'opacity-40'">
            <input
              type="time"
              v-model="rows[day].start"
              :disabled="!rows[day].working || isSaving"
              :class="[TIME_INPUT_CLASS, rowErrors[day] ? 'border-red-400' : 'border-[#2A2018]']"
            />
            <span class="text-[#524840] text-sm">–</span>
            <input
              type="time"
              v-model="rows[day].end"
              :disabled="!rows[day].working || isSaving"
              :class="[TIME_INPUT_CLASS, rowErrors[day] ? 'border-red-400' : 'border-[#2A2018]']"
            />
          </div>
        </div>
        <p v-if="rowErrors[day]" class="mt-1 text-xs text-red-500 sm:ml-32">{{ rowErrors[day] }}</p>
      </div>
    </div>

    <div
      v-if="successMessage"
      class="mt-6 px-4 py-3 bg-green-900/20 border border-green-800 rounded-xl text-sm text-green-400"
    >
      {{ successMessage }}
    </div>
    <div
      v-if="errorMessage"
      class="mt-6 px-4 py-3 bg-red-900/20 border border-red-800 rounded-xl text-sm text-red-400"
    >
      {{ errorMessage }}
    </div>

    <div class="mt-6 flex justify-end">
      <button
        type="button"
        @click="handleSubmit"
        :disabled="isSaving"
        class="px-6 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] text-sm rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ isSaving ? $t('settings.saving') : $t('settings.save_button') }}
      </button>
    </div>
  </div>
</template>
